import { useEffect, useRef, useState, createContext, useContext } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { RTL_LANGS } from "@/i18n";
import { LANG_SLUGS, stripLangPrefix } from "@/components/LangLink";
import { installContactClickTracking, trackVirtualPageView } from "@/analytics";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LoginModal from "@/components/LoginModal";
import { PublicLiveChat } from "@/components/PublicLiveChat";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Membership from "@/pages/Membership";
import Blog from "@/pages/Blog";
import Contact from "@/pages/Contact";
import Portal from "@/pages/Portal";
import Admin from "@/pages/Admin";
import ZohoSetup from "@/pages/ZohoSetup";
import Activate from "@/pages/Activate";

const LoginModalContext = createContext(() => {});
export const useLoginModal = () => useContext(LoginModalContext);

const AuthContext = createContext({ user: null, setUser: () => {} });
export const useAuth = () => useContext(AuthContext);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const RouteDirection = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  useEffect(() => {
    const base = stripLangPrefix(pathname);
    const isAdminRoute = base.startsWith("/admin") || base.startsWith("/zoho-connect");
    const baseLanguage = (i18n.language || "en").split("-")[0];
    document.documentElement.lang = isAdminRoute ? "en" : baseLanguage;
    document.documentElement.dir = !isAdminRoute && RTL_LANGS.includes(baseLanguage) ? "rtl" : "ltr";
  }, [pathname, i18n.language]);
  return null;
};

const AnalyticsRouteObserver = () => {
  const { pathname, search } = useLocation();
  const previousPath = useRef(null);
  useEffect(() => {
    const pagePath = `${pathname}${search || ""}`;
    if (previousPath.current === pagePath) return;
    const isInitialRoute = previousPath.current === null;
    previousPath.current = pagePath;
    if (!isInitialRoute) {
      trackVirtualPageView({ pathname, search, title: document.title });
    }
  }, [pathname, search]);
  return null;
};


const PublicLiveChatGate = () => {
  const { pathname } = useLocation();
  const base = stripLangPrefix(pathname);
  if (base.startsWith("/portal") || base.startsWith("/admin") || base.startsWith("/zoho-connect")) return null;
  return <PublicLiveChat />;
};


// Layout shared by all public pages. When the URL carries a language prefix
// (/es, /pt, ...), the shell syncs i18n to that language; the unprefixed shell
// leaves the stored/detected language untouched.
function LangShell({ lang }) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (lang && (i18n.language || "en").split("-")[0] !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

const publicChildren = (prefixed) => [
  prefixed ? (
    <Route key="home" index element={<Home />} />
  ) : (
    <Route key="home" path="/" element={<Home />} />
  ),
  <Route key="about" path={prefixed ? "about" : "/about"} element={<About />} />,
  <Route key="about-us" path={prefixed ? "about-us" : "/about-us"} element={<Navigate to={prefixed ? "about" : "/about"} replace />} />,
  <Route key="services" path={prefixed ? "services" : "/services"} element={<Services />} />,
  <Route key="service" path={prefixed ? "services/:id" : "/services/:id"} element={<ServiceDetail />} />,
  <Route key="membership" path={prefixed ? "membership" : "/membership"} element={<Membership />} />,
  <Route key="blog" path={prefixed ? "blog" : "/blog"} element={<Blog />} />,
  <Route key="contact" path={prefixed ? "contact" : "/contact"} element={<Contact />} />,
  <Route key="portal" path={prefixed ? "portal" : "/portal"} element={<Portal />} />,
];


function App() {
  const { i18n } = useTranslation();
  const [loginOpen, setLoginOpen] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dc_user"));
    } catch {
      return null;
    }
  });

  useEffect(() => installContactClickTracking(), []);


  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <LoginModalContext.Provider value={() => setLoginOpen(true)}>
      <BrowserRouter>
        <ScrollToTop />
        <RouteDirection />
        <AnalyticsRouteObserver />
        <div className="bg-ink text-white min-h-screen font-sans" data-language={i18n.language}>
          <Routes>
            <Route path="/admin" element={<Admin />} />
            <Route path="/zoho-connect" element={<ZohoSetup />} />
            <Route path="/activate" element={<Activate />} />
            {LANG_SLUGS.map((l) => (
              <Route key={l} path={`/${l}`} element={<LangShell lang={l} />}>
                {publicChildren(true)}
                <Route path="*" element={<Navigate to={`/${l}`} replace />} />
              </Route>
            ))}
            <Route element={<LangShell lang={null} />}>
              {publicChildren(false)}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
          <PublicLiveChatGate />
          <Toaster theme="dark" position="bottom-right" />
        </div>
      </BrowserRouter>
      </LoginModalContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
